import { createContext, useLayoutEffect, useState } from 'react';

import { FeatureFlag } from '@revelio/core';

import {
  useUnleashFlag,
  useUnleashFlags,
} from '../../../shared/unleash-flags/use-unleash-flag';
import {
  PageType,
  type ReportPageConfig,
} from '../../CustomDash/components/PageConfigProvider/types';
import { usePageManager } from '../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { getLocalReportTemplate } from '../../CustomDash/templates/report-templates-registry';
import { getLocalTemplates } from '../../CustomDash/utils/page-config-lookup';
import {
  globalAssistantReportLaunchPendingStore,
  useGlobalAssistantReportLaunchPendingStore,
} from '../../global-ai-assistant/global-ai-assistant-report-launch-pending';
import { getPromotedTemplateIds } from '../utils/report-template-promotions';
import { useAllReports } from './report-builder.repository';

/**
 * This context is used to manage the custom report builder flow while other reports uses Redux for state management.
 */
export const CustomReportBuilderContext = createContext<{
  customReportsIsEnabled: boolean;
  experimentalReportsIsEnabled: boolean;
  customReportTemplates: ReportPageConfig[];
  experimentalReportTemplates: ReportPageConfig[];
  isLoadingReportTemplates: boolean;
  selectedReportTemplate: ReportPageConfig | null;
  setReportTemplate: (reportTemplate: ReportPageConfig | null) => void;
}>({
  customReportsIsEnabled: false,
  experimentalReportsIsEnabled: false,
  customReportTemplates: [],
  experimentalReportTemplates: [],
  isLoadingReportTemplates: false,
  selectedReportTemplate: null,
  setReportTemplate: () => {
    throw new Error('setReportTemplate not implemented on default context');
  },
});

/**
 * This context provider is used to manage the custom report builder flow while other reports uses Redux for state management.
 */
export const CustomReportBuilderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const customReportsIsEnabled = useUnleashFlag(FeatureFlag.CustomReports);
  const experimentalReportsIsEnabled = useUnleashFlag(
    FeatureFlag.ExperimentalReports
  );
  const allFlags = useUnleashFlags();
  const promotedTemplateIds = getPromotedTemplateIds(
    allFlags,
    customReportsIsEnabled
  );
  const { pages, loading: isLoadingReportTemplates } = usePageManager();
  const localTemplates = getLocalTemplates();
  const localReportTemplates = localTemplates.filter(
    (page) => page.type === PageType.REPORT
  ) as ReportPageConfig[];
  const remoteReportPages = pages.filter(
    (page) => page.type === PageType.REPORT
  );
  const remoteReportIds = new Set(remoteReportPages.map((p) => p.id));
  const customReportTemplates = remoteReportPages;
  const experimentalReportTemplates = localReportTemplates.filter(
    (t) => !remoteReportIds.has(t.id) && !promotedTemplateIds.has(t.id)
  );
  const pendingPromotedTemplateId = useGlobalAssistantReportLaunchPendingStore(
    (state) => state.promotedTemplateId
  );
  const mwReportCount = useAllReports().length;
  const pendingReportTemplate = pendingPromotedTemplateId
    ? (getLocalReportTemplate(pendingPromotedTemplateId) ?? null)
    : null;
  const [selectedReportTemplate, setSelectedReportTemplate] =
    useState<ReportPageConfig | null>(() => {
      const pendingTemplateId =
        globalAssistantReportLaunchPendingStore.getState().promotedTemplateId;
      if (!pendingTemplateId) {
        return null;
      }
      return getLocalReportTemplate(pendingTemplateId) ?? null;
    });
  const activeReportTemplate = pendingReportTemplate ?? selectedReportTemplate;

  useLayoutEffect(() => {
    if (pendingPromotedTemplateId) {
      const template = getLocalReportTemplate(pendingPromotedTemplateId);
      if (template) {
        setSelectedReportTemplate(template);
      }
      return;
    }

    if (mwReportCount > 0) {
      setSelectedReportTemplate(null);
    }
  }, [pendingPromotedTemplateId, mwReportCount]);

  const selectReportTemplate = (reportTemplate: ReportPageConfig | null) => {
    setSelectedReportTemplate(reportTemplate);
  };

  return (
    <CustomReportBuilderContext.Provider
      value={{
        customReportsIsEnabled,
        experimentalReportsIsEnabled,
        customReportTemplates,
        experimentalReportTemplates,
        isLoadingReportTemplates,
        selectedReportTemplate: activeReportTemplate,
        setReportTemplate: selectReportTemplate,
      }}
    >
      {children}
    </CustomReportBuilderContext.Provider>
  );
};
